import React from 'react';
import { LeaderboardEntry } from './types';
import { formatTime } from './utils/helpers';
import KBCUserProfile from './KBCUserProfile';

interface LeaderboardProps {
  entries: LeaderboardEntry[];
  onBack: () => void;
  isLive?: boolean;
}

const Leaderboard: React.FC<LeaderboardProps> = ({
  entries,
  onBack,
  isLive = true,
}) => {
  const getMedalEmoji = (rank: number): string => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return '';
    }
  };

  // Sort entries: 
  // 1. Most correct answers first (descending)
  // 2. If tied, fastest time first (ascending)
  // 3. Recalculate ranks after sorting
  const sortedEntries = React.useMemo(() => {
    const sorted = [...entries].sort((a, b) => {
      // Primary sort: most correct answers first
      if (b.questionsCorrect !== a.questionsCorrect) {
        return b.questionsCorrect - a.questionsCorrect;
      }
      // Secondary sort: fastest time first (lower time = better)
      return a.time - b.time;
    });

    // Recalculate ranks after sorting
    return sorted.map((entry, index) => ({
      ...entry,
      rank: index + 1,
    }));
  }, [entries]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-kbc-dark via-kbc-dark-accent to-kbc-dark p-4 overflow-y-auto relative">
      {/* User Profile - Top Left (Small) */}
      <KBCUserProfile size="small" position="top-left" />
      <div className="max-w-4xl mx-auto py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              🏆 Leaderboard
            </h1>
            {isLive && (
              <span className="bg-kbc-success/20 text-kbc-success px-3 py-1 rounded-full text-sm font-semibold animate-pulse">
                Live
              </span>
            )}
          </div>
          <p className="text-white/70 text-sm mt-2">
            ⚡ Ranked by correct answers, then fastest time wins!
          </p>
        </div>

        {/* Leaderboard Table */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 shadow-2xl overflow-hidden mb-8">
          {/* Table Header */}
          <div className="bg-kbc-gold/20 border-b border-kbc-gold/30 p-4">
            <div className="grid grid-cols-12 gap-3 text-white font-bold text-sm">
              <div className="col-span-2 md:col-span-1">Rank</div>
              <div className="col-span-5 md:col-span-4">Player</div>
              <div className="col-span-3 md:col-span-2 text-right">Correct</div>
              <div className="col-span-2 md:col-span-2 text-right hidden md:block">Time</div>
              <div className="col-span-2 md:col-span-3 text-right hidden md:block">Prize</div>
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-white/10">
            {sortedEntries.length === 0 ? (
              <div className="p-8 text-center text-white/70">
                No entries yet. Be the first to play!
              </div>
            ) : (
              sortedEntries.map((entry, index) => (
                <div
                  key={entry.userId}
                  className={`p-4 transition-colors ${
                    index < 3
                      ? 'bg-kbc-gold/10 hover:bg-kbc-gold/20'
                      : 'hover:bg-white/5'
                  }`}
                >
                  <div className="grid grid-cols-12 gap-3 items-center">
                    {/* Rank */}
                    <div className="col-span-2 md:col-span-1">
                      <div className="flex items-center">
                        <span className="text-2xl mr-1">
                          {getMedalEmoji(entry.rank)}
                        </span>
                        <span className="text-white font-bold">
                          {entry.rank <= 3 ? '' : entry.rank}
                        </span>
                      </div>
                    </div>

                    {/* Player Name */}
                    <div className="col-span-5 md:col-span-4">
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-kbc-gold/30 flex items-center justify-center text-white font-bold text-sm mr-2">
                          {entry.displayName.charAt(0).toUpperCase()}
                        </div>
                        <p className="text-white font-semibold">
                          {entry.displayName}
                        </p>
                      </div>
                    </div>

                    {/* Questions Correct */}
                    <div className="col-span-3 md:col-span-2 text-right">
                      <p className="text-kbc-gold font-bold text-lg">
                        {entry.questionsCorrect}
                      </p>
                    </div>

                    {/* Time (desktop only) */}
                    <div className="col-span-2 md:col-span-2 text-right hidden md:block">
                      <p className="text-white/70 text-sm">
                        {formatTime(entry.time)}
                      </p>
                    </div>

                    {/* Prize (desktop only) */}
                    <div className="col-span-2 md:col-span-3 text-right hidden md:block">
                      {entry.prizeWon ? (
                        <div>
                          <p className="text-kbc-amber text-sm font-semibold">
                            Tier {entry.prizeLevel}
                          </p>
                          <p className="text-white/60 text-xs truncate">
                            {entry.prizeWon}
                          </p>
                        </div>
                      ) : (
                        <p className="text-white/40 text-sm">—</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Status Badge - show for incomplete/walked out/cashed out */}
                  {entry.status && entry.status !== 'Completed' && (
                    <div className="mt-2 flex justify-end md:justify-start">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        entry.status === 'Walked Out' 
                          ? 'bg-red-500/20 text-red-300 border border-red-500/30'
                          : entry.status === 'Cashed Out'
                          ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                          : 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
                      }`}>
                        {entry.status}
                      </span>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Stats Summary */}
        {sortedEntries.length > 0 && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center border border-white/20">
              <p className="text-white/70 text-sm mb-1">Total Players</p>
              <p className="text-2xl font-bold text-white">
                {sortedEntries.length}
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center border border-white/20">
              <p className="text-white/70 text-sm mb-1">Best Score</p>
              <p className="text-2xl font-bold text-kbc-gold">
                {sortedEntries.length > 0 ? `${sortedEntries[0].questionsCorrect}` : '0'}
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center border border-white/20">
              <p className="text-white/70 text-sm mb-1">Avg Correct</p>
              <p className="text-2xl font-bold text-kbc-amber">
                {sortedEntries.length > 0
                  ? Math.round(
                      sortedEntries.reduce((sum, e) => sum + e.questionsCorrect, 0) / sortedEntries.length
                    ).toString()
                  : '0'}
              </p>
            </div>
          </div>
        )}

        {/* Back Button */}
        <button
          onClick={onBack}
          className="w-full bg-kbc-gold hover:bg-kbc-amber text-maersk-navy font-bold text-lg py-4 rounded-lg shadow-lg transform transition hover:scale-105 active:scale-95"
        >
          ← Back to KBC Home
        </button>

        {/* Info Note */}
        {/* {isLive && (
          <p className="text-center text-white/60 text-sm mt-4">
            💡 Leaderboard updates automatically every 30 seconds
          </p>
        )} */}
      </div>
    </div>
  );
};

export default Leaderboard;
